import React, {FC, useCallback, useRef, useState} from 'react';
import {useRect} from 'react-viewport-utils';
import styled from 'styled-components';

import {HorizontalSlider} from '../../molecules/HorizontalSlider/HorizontalSlider';
import {
  ProductImage,
  ProductImageProps,
} from '../../molecules/ProductImage/ProductImage';

import {COLORS} from '../../../constants/colors';
import {mediaExtraLargeScreens} from '../../../constants/customMedia';
import {SliderControlProps} from '../../molecules/HorizontalSlider/types';
import {THUMBNAIL_WIDTH, ThumbnailNav} from './ThumbnailNav';

interface Props {
  images: Array<ProductImageProps>;
  currentOuterIndex?: number;
}

export const ProductImageDetails: FC<Props> = ({images, currentOuterIndex}) => {
  const [scaledIndex, setScaled] = useState<false | number>(false);
  const SlideControlComponent = useCallback(
    (props: SliderControlProps) => <ThumbnailNav images={images} {...props} />,
    [images],
  );
  const {ref: wrapperRef, scale, originY, setMouseY} = useZoom();

  const onMouseMove = useCallback(
    (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      setMouseY(ev.nativeEvent.clientY);
    },
    [setMouseY],
  );

  return (
    <Wrapper
      data-testid="ProductImageDetails"
      onMouseMove={onMouseMove}
      ref={wrapperRef}
    >
      <StyledHorizontalSlider
        slideCount={1}
        currentOuterIndex={currentOuterIndex}
        constrained
        disableHeaderControl
        data-testid="ProductImageDetailsSlider"
        SlideControlComponentBottom={SlideControlComponent}
        theme="dark"
        enableKeyboardNavigation
      >
        {images.map(({alt, src, padded}, index) => {
          const isScaled = scaledIndex === index;
          return (
            <Slide key={index}>
              <ProductWrapper
                style={{
                  transformOrigin: `50% ${originY}%`,
                  transform: isScaled ? `scale(${scale})` : 'none',
                }}
                onClick={() => setScaled(isScaled ? false : index)}
              >
                <ProductImageSlide
                  alt={alt}
                  breakpoints={
                    isScaled
                      ? IMAGE_BREAKPOINTS_SCALED
                      : IMAGE_BREAKPOINTS_DEFAULT
                  }
                  data-testid={`ProductImageDetails_Slide_${index}`}
                  padded={padded}
                  src={src}
                  $scaled={isScaled}
                  loading={isScaled ? 'eager' : 'lazy'}
                  decoding={isScaled ? 'sync' : 'async'}
                />
              </ProductWrapper>
            </Slide>
          );
        })}
      </StyledHorizontalSlider>
    </Wrapper>
  );
};

const IMAGE_BREAKPOINTS_SCALED = [{columns: 1}];
const IMAGE_BREAKPOINTS_DEFAULT = [
  {breakpoint: mediaExtraLargeScreens, columns: 2},
  {columns: 1},
];

// the amount of space that should be reserved top + bottom
const SAFETY_MULTIPLICATOR = 1.4;
const CENTER = 0.5;
const IMAGE_RATIO = 3 / 4;
const useZoom = () => {
  const [mouseY, setMouseY] = useState(CENTER);
  const ref = useRef<HTMLDivElement>(null);
  const {
    height: wrapperHeight,
    width: wrapperWidth,
    top,
  } = useRect(ref, {
    priority: 'low',
  }) || {
    height: NaN,
    width: NaN,
    top: NaN,
  };
  const relativeY = mouseY - top;
  const relativeYRatio = relativeY / wrapperHeight;
  const diffToCenter = relativeYRatio - CENTER;
  const calculatedSafetyYRatio = diffToCenter * SAFETY_MULTIPLICATOR + CENTER;
  const effectiveRelativeSafetyYRatio = Math.min(
    1,
    Math.max(0, calculatedSafetyYRatio),
  );

  const pictureWidth = wrapperWidth - THUMBNAIL_WIDTH;

  const imageWidth = wrapperHeight * IMAGE_RATIO;
  const scale = pictureWidth / imageWidth;

  return {
    scale,
    originY: effectiveRelativeSafetyYRatio * 100,
    setMouseY,
    ref,
  };
};

const Wrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: ${COLORS.White};
`;

const StyledHorizontalSlider = styled(HorizontalSlider)`
  height: 100%;
  display: flex;
  flex-direction: row-reverse;
`;

const Slide = styled.div``;
const ProductWrapper = styled.div`
  width: 75vh;
  margin: auto;
  transition: transform 0.15s ease-in-out;
`;

const ProductImageSlide = styled(ProductImage)<{$scaled: boolean}>`
  cursor: ${({$scaled}) => ($scaled ? 'zoom-out' : 'zoom-in')};
`;
